const OPENWEATHER_API_KEY = '6ee65d312f0b2877ea05da79c8393dd5';

export interface JarvisWeatherResult {
  temperature: number;
  condition: string;
  location: string;
  high: number;
  low: number;
  icon: string; // OpenWeatherMap icon code or emoji
  feelsLike?: number;
  humidity?: number;
  windSpeed?: number;
  windDirection?: string;
  pressure?: number;
  visibility?: number;
  uv?: number;
  cloudCover?: number;
  precipitation?: number;
  gustSpeed?: number;
  region?: string;
  country?: string;
  timezone?: string;
  localTime?: string;
  coordinates?: { lat: number; lon: number };
  forecast?: Array<{
    date: string;
    maxTemp: number;
    minTemp: number;
    condition: string;
    icon: string;
    precipitation: number;
    humidity: number;
  }>;
  astronomy?: {
    sunrise: string;
    sunset: string;
    moonrise: string;
    moonset: string;
    moonPhase: string;
    moonIllumination: string;
  };
  aqi?: number;
  airQuality?: string;
}

const emojiForOpenWeather = (code: number, main: string): string => {
  if (code >= 200 && code < 300) return '⛈️'; // Thunderstorm
  if (code >= 300 && code < 400) return '🌦️'; // Drizzle
  if (code >= 500 && code < 600) return '🌧️'; // Rain
  if (code >= 600 && code < 700) return '❄️'; // Snow
  if (code >= 700 && code < 800) return '🌫️'; // Atmosphere (Mist/Fog/Dust)
  if (code === 800) return '☀️'; // Clear
  if (code === 801) return '🌤️'; // Few clouds
  if (code >= 802 && code <= 804) return '☁️'; // Clouds
  return '🌡️';
};

const getOpenMeteoEmoji = (code: number): string => {
  if (code === 0) return '☀️'; // Clear sky
  if (code >= 1 && code <= 3) return '🌤️'; // Mainly clear, partly cloudy, overcast
  if (code >= 45 && code <= 48) return '🌫️'; // Fog
  if (code >= 51 && code <= 55) return '🌦️'; // Drizzle
  if (code >= 61 && code <= 65) return '🌧️'; // Rain
  if (code >= 71 && code <= 75) return '❄️'; // Snow
  if (code >= 80 && code <= 82) return '🌧️'; // Rain showers
  if (code >= 95 && code <= 99) return '⛈️'; // Thunderstorm
  return '🌡️';
};

const getOpenMeteoDesc = (code: number): string => {
  if (code === 0) return 'Clear';
  if (code === 1) return 'Mainly Clear';
  if (code === 2) return 'Partly Cloudy';
  if (code === 3) return 'Overcast';
  if (code >= 45 && code <= 48) return 'Fog';
  if (code >= 51 && code <= 55) return 'Drizzle';
  if (code >= 61 && code <= 65) return 'Rain';
  if (code >= 71 && code <= 75) return 'Snow';
  if (code >= 80 && code <= 82) return 'Showers';
  if (code >= 95 && code <= 99) return 'Thunderstorm';
  return 'Cloudy';
};

const getWindDirection = (degree: number): string => {
  const sectors = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round(degree / 22.5) % 16;
  return sectors[index];
};

const getUserLocation = async (): Promise<{ lat: number; lon: number } | null> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.warn('Geolocation is not supported by this browser');
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        });
      },
      (error) => {
        console.warn('Error getting user location:', error.message);
        resolve(null);
      },
      {
        timeout: 5000,
        maximumAge: 300000, // Cache for 5 minutes
      }
    );
  });
};

export const fetchJarvisWeather = async (city?: string): Promise<JarvisWeatherResult | null> => {
  try {
    let lat = 0;
    let lon = 0;
    let resolvedName = 'Unknown Location';
    let countryCode = '';
    let stateName = '';

    if (city) {
      // Check if city is a coordinate string like "lat,lon"
      const coordMatch = city.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
      if (coordMatch) {
        lat = parseFloat(coordMatch[1]);
        lon = parseFloat(coordMatch[2]);
        resolvedName = `${lat.toFixed(2)}, ${lon.toFixed(2)}`;
      } else {
        // Geocode city name using OpenWeatherMap Geocoding API
        const geoUrl = `https://api.openweathermap.org/geo/1.0/direct?q=${encodeURIComponent(city)}&limit=1&appid=${OPENWEATHER_API_KEY}`;
        const geoResp = await fetch(geoUrl);
        if (!geoResp.ok) {
          throw new Error('Geocoding request failed');
        }
        const geoData = await geoResp.json();
        if (!geoData || geoData.length === 0) {
          console.warn('No coordinates found for city:', city);
          return null;
        }
        lat = geoData[0].lat;
        lon = geoData[0].lon;
        resolvedName = geoData[0].name;
        countryCode = geoData[0].country || '';
        stateName = geoData[0].state || '';
      }
    } else {
      // Auto-detect coordinates
      const coords = await getUserLocation();
      if (coords) {
        lat = coords.lat;
        lon = coords.lon;
      } else {
        // Fallback coordinates (New York)
        lat = 40.7128;
        lon = -74.0060;
      }
      
      // Reverse geocode to get name
      const revUrl = `https://api.openweathermap.org/geo/1.0/reverse?lat=${lat}&lon=${lon}&limit=1&appid=${OPENWEATHER_API_KEY}`;
      try {
        const revResp = await fetch(revUrl);
        if (revResp.ok) {
          const revData = await revResp.json();
          if (revData && revData.length > 0) {
            resolvedName = revData[0].name;
            countryCode = revData[0].country || '';
            stateName = revData[0].state || '';
          }
        }
      } catch (e) {
        console.warn('Reverse geocoding failed, using coordinates');
        resolvedName = `${lat.toFixed(2)}, ${lon.toFixed(2)}`;
      }
    }

    // 1. Fetch current weather from OpenWeatherMap
    const curWeatherUrl = `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&units=metric&appid=${OPENWEATHER_API_KEY}`;
    const curResp = await fetch(curWeatherUrl);
    if (!curResp.ok) {
      throw new Error('Current weather request failed');
    }
    const curData = await curResp.json();

    // 2. Fetch daily forecast from Open-Meteo (More granular and completely free)
    let forecastData: any[] = [];
    try {
      const meteoUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,relative_humidity_2m_mean,weathercode&timezone=auto`;
      const meteoResp = await fetch(meteoUrl);
      if (meteoResp.ok) {
        const meteoData = await meteoResp.json();
        const daily = meteoData.daily;
        if (daily && daily.time) {
          forecastData = daily.time.map((timeStr: string, idx: number) => ({
            date: timeStr,
            maxTemp: Math.round(daily.temperature_2m_max[idx]),
            minTemp: Math.round(daily.temperature_2m_min[idx]),
            condition: getOpenMeteoDesc(daily.weathercode[idx]),
            icon: getOpenMeteoEmoji(daily.weathercode[idx]),
            precipitation: daily.precipitation_sum[idx] || 0,
            humidity: Math.round(daily.relative_humidity_2m_mean[idx] || 50),
          }));
        }
      }
    } catch (meteoErr) {
      console.warn('Open-Meteo forecast failed, generating fallback forecast', meteoErr);
    }

    // 3. Fetch Air Quality Index from OpenWeatherMap
    let aqi = 1;
    try {
      const airPollutionUrl = `https://api.openweathermap.org/data/2.5/air_pollution?lat=${lat}&lon=${lon}&appid=${OPENWEATHER_API_KEY}`;
      const aqiResp = await fetch(airPollutionUrl);
      if (aqiResp.ok) {
        const aqiData = await aqiResp.json();
        aqi = aqiData.list?.[0]?.main?.aqi || 1;
      }
    } catch (aqiErr) {
      console.warn('AQI fetch failed');
    }

    const aqiLabels = ['Good', 'Fair', 'Moderate', 'Poor', 'Very Poor'];
    const airQuality = aqiLabels[aqi - 1] || 'Good';

    // Parse weather condition details
    const conditionObj = curData.weather?.[0] || { id: 800, main: 'Clear', description: 'clear sky', icon: '01d' };
    const temp = Math.round(curData.main.temp ?? 15);
    const feelsLike = Math.round(curData.main.feels_like ?? temp);
    const humidity = curData.main.humidity ?? 50;
    const windSpeed = Math.round((curData.wind?.speed ?? 0) * 3.6); // m/s to km/h
    const windDirection = getWindDirection(curData.wind?.deg ?? 0);
    const pressure = curData.main.pressure ?? 1013;
    const visibility = curData.visibility ? Math.round(curData.visibility / 1000) : 10; // meters to km
    const cloudCover = curData.clouds?.all ?? 0;
    const icon = conditionObj.icon || '01d';

    const sunriseTime = curData.sys?.sunrise || 0;
    const sunsetTime = curData.sys?.sunset || 0;
    const sunriseStr = sunriseTime ? new Date(sunriseTime * 1000).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '06:00 AM';
    const sunsetStr = sunsetTime ? new Date(sunsetTime * 1000).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '06:00 PM';

    // 4. Fetch astronomy data from WeatherAPI.com
    let astronomyInfo = {
      sunrise: sunriseStr,
      sunset: sunsetStr,
      moonrise: '08:00 PM',
      moonset: '07:00 AM',
      moonPhase: 'Waning Gibbous',
      moonIllumination: '75%',
    };

    try {
      const astroUrl = `https://api.weatherapi.com/v1/astronomy.json?key=5d87a8c0c7e34a16a8841647251610&q=${lat},${lon}`;
      const astroResp = await fetch(astroUrl);
      if (astroResp.ok) {
        const astroData = await astroResp.json();
        const astro = astroData.astronomy?.astro;
        if (astro) {
          astronomyInfo = {
            sunrise: astro.sunrise || sunriseStr,
            sunset: astro.sunset || sunsetStr,
            moonrise: astro.moonrise || '08:00 PM',
            moonset: astro.moonset || '07:00 AM',
            moonPhase: astro.moon_phase || 'Waning Gibbous',
            moonIllumination: astro.moon_illumination || '75%',
          };
        }
      }
    } catch (e) {
      console.warn('Astro fetch failed in Jarvis weather service', e);
    }

    return {
      temperature: temp,
      condition: conditionObj.main,
      location: stateName ? `${resolvedName}, ${stateName}` : resolvedName,
      high: forecastData[0] ? forecastData[0].maxTemp : temp + 3,
      low: forecastData[0] ? forecastData[0].minTemp : temp - 3,
      icon,
      feelsLike,
      humidity,
      windSpeed,
      windDirection,
      pressure,
      visibility,
      uv: 0,
      cloudCover,
      precipitation: forecastData[0]?.precipitation || 0,
      gustSpeed: curData.wind?.gust ? Math.round(curData.wind.gust * 3.6) : undefined,
      region: stateName || undefined,
      country: countryCode || undefined,
      timezone: undefined,
      localTime: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
      coordinates: { lat, lon },
      forecast: forecastData.slice(0, 5),
      astronomy: astronomyInfo,
      aqi,
      airQuality,
    };
  } catch (error) {
    console.error('Error fetching combined Jarvis weather:', error);
    return null;
  }
};
